import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Bookmark, Category, ImportData } from '../types/bookmark'
import { DefaultTheme } from 'styled-components'

interface BookmarkState {
  bookmarks: Bookmark[]
  categories: Category[]
  theme: DefaultTheme['mode']
  toggleTheme: () => void
  selectedBookmarks: Set<string>
  addBookmark: (bookmark: Omit<Bookmark, 'id' | 'createdAt' | 'updatedAt' | 'tags' | 'isFavorite'>) => void
  removeBookmark: (id: string) => void
  removeBookmarks: (ids: string[]) => void
  updateBookmark: (id: string, bookmark: Partial<Bookmark>) => void
  toggleBookmarkSelection: (id: string) => void
  clearBookmarkSelection: () => void
  toggleBookmarkFavorite: (id: string) => void
  addTagToBookmark: (id: string, tag: string) => void
  removeTagFromBookmark: (id: string, tag: string) => void
  addCategory: (category: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>) => void
  removeCategory: (id: string) => void
  updateCategory: (id: string, category: Partial<Category>) => void
  importData: (data: ImportData) => void
  reorderBookmarks: (startIndex: number, endIndex: number) => void
}

export const useBookmarkStore = create<BookmarkState>()(
  persist(
    (set) => ({
      bookmarks: [],
      categories: [],
      theme: 'light',
      toggleTheme: () => set(state => ({ theme: state.theme === 'light' ? 'dark' : 'light' })),
      selectedBookmarks: new Set<string>(),

      addBookmark: (bookmark) => {
        const newBookmark: Bookmark = {
          ...bookmark,
          id: crypto.randomUUID(),
          tags: [],
          isFavorite: false,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        }
        set((state) => ({
          bookmarks: [...state.bookmarks, newBookmark],
        }))
      },

      removeBookmark: (id) => {
        set((state) => ({
          bookmarks: state.bookmarks.filter((bookmark) => bookmark.id !== id),
          selectedBookmarks: new Set([...state.selectedBookmarks].filter(bookmarkId => bookmarkId !== id)),
        }))
      },

      removeBookmarks: (ids) => {
        set((state) => ({
          bookmarks: state.bookmarks.filter((bookmark) => !ids.includes(bookmark.id)),
          selectedBookmarks: new Set(),
        }))
      },

      updateBookmark: (id, bookmark) => {
        set((state) => ({
          bookmarks: state.bookmarks.map((b) =>
            b.id === id
              ? { ...b, ...bookmark, updatedAt: Date.now() }
              : b
          ),
        }))
      },

      toggleBookmarkSelection: (id) => {
        set((state) => {
          const newSelection = new Set(state.selectedBookmarks)
          if (newSelection.has(id)) {
            newSelection.delete(id)
          } else {
            newSelection.add(id)
          }
          return { selectedBookmarks: newSelection }
        })
      },

      clearBookmarkSelection: () => {
        set({ selectedBookmarks: new Set() })
      },

      toggleBookmarkFavorite: (id) => {
        set((state) => ({
          bookmarks: state.bookmarks.map((b) =>
            b.id === id
              ? { ...b, isFavorite: !b.isFavorite, updatedAt: Date.now() }
              : b
          ),
        }))
      },

      addTagToBookmark: (id, tag) => {
        set((state) => ({
          bookmarks: state.bookmarks.map((b) =>
            b.id === id && !b.tags.includes(tag)
              ? { ...b, tags: [...b.tags, tag], updatedAt: Date.now() }
              : b
          ),
        }))
      },

      removeTagFromBookmark: (id, tag) => {
        set((state) => ({
          bookmarks: state.bookmarks.map((b) =>
            b.id === id
              ? { ...b, tags: b.tags.filter((t) => t !== tag), updatedAt: Date.now() }
              : b
          ),
        }))
      },

      addCategory: (category) => {
        const newCategory: Category = {
          ...category,
          id: crypto.randomUUID(),
          createdAt: Date.now(),
          updatedAt: Date.now(),
        }
        set((state) => ({
          categories: [...state.categories, newCategory],
        }))
      },

      removeCategory: (id) => {
        set((state) => ({
          categories: state.categories.filter((category) => category.id !== id),
          bookmarks: state.bookmarks.map((bookmark) =>
            bookmark.categoryId === id
              ? { ...bookmark, categoryId: undefined }
              : bookmark
          ),
        }))
      },

      updateCategory: (id, category) => {
        set((state) => ({
          categories: state.categories.map((c) =>
            c.id === id
              ? { ...c, ...category, updatedAt: Date.now() }
              : c
          ),
        }))
      },

      importData: (data) => {
        set((state) => {
          // 合并现有数据和导入数据
          const existingIds = new Set(state.bookmarks.map(b => b.id))
          const existingCategoryIds = new Set(state.categories.map(c => c.id))

          const newBookmarks = data.bookmarks.filter(b => !existingIds.has(b.id))
          const newCategories = data.categories.filter(c => !existingCategoryIds.has(c.id))

          return {
            bookmarks: [...state.bookmarks, ...newBookmarks],
            categories: [...state.categories, ...newCategories],
          }
        })
      },

      reorderBookmarks: (startIndex: number, endIndex: number) => {
        set((state) => {
          const result = Array.from(state.bookmarks)
          const [removed] = result.splice(startIndex, 1)
          result.splice(endIndex, 0, removed)
          return { bookmarks: result }
        })
      },
    }),
    {
      name: 'bookmark-storage',
      partialize: (state) => ({
        bookmarks: state.bookmarks,
        categories: state.categories,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.selectedBookmarks = new Set<string>()
        }
      },
    }
  )
) 